import { FormattedMessage, useIntl } from "react-intl";

// Mui Components
import makeStyles from "@mui/styles/makeStyles";
import { Box, IconButton, Typography, Theme, Button } from "@mui/material";
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";

// Components
import Modal from "components/Modal";
import { LinkButton } from "components/Buttons";
import NavButton from "components/HeaderNavigation/NavButton";
import CustomIcon from "components/CustomIcon";
import WorkHistoryTimeline from "components/SetupWizard/WorkHistoryTimeline";
import icons from "enums/icons";
import { ProfileEntry } from "components/SetupWizard/CreateYourProfile";

// Hooks
import useYourProfile from "./useYourProfile";

const useStyles = makeStyles((theme: Theme) => ({
    addRoleIcon: {
        fontSize: 16,
        color: `${theme.palette.button.active.to} !important`,
    },
    title: {
        fontSize: 19,
        fontWeight: 600,
        color: theme.palette.text.primary,
        textAlign: "start",
        marginBottom: theme.spacing(2),
    },
    progressBar: {
        backgroundColor: theme.palette.neutrals.light,
        borderRadius: 8,
        height: 8,
        "& .MuiLinearProgress-barColorPrimary": {
            backgroundColor: theme.palette.secondary.main,
        },
    },
    workHistoryWrapper: {
        flex: "1 1 0px",
        height: "100%",
        padding: theme.spacing(2),
        boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
        borderRadius: "6px",
    },
    drawerBlock: {
        marginBottom: theme.spacing(1),
        "&:last-of-type": {
            marginBottom: theme.spacing(0),
        },
    },
    drawerBlockTitle: {
        fontWeight: 600,
        marginTop: 0,
        marginBottom: 20,
    },
}));

const YourProfile = ({
    personId,
    title,
    isEditable = true,
}: {
    personId: number | undefined;
    title: string;
    isEditable?: boolean;
}): JSX.Element => {
    const intl = useIntl();
    const classes = useStyles();

    const {
        wrapperRef,
        scrollerWrapperRef,
        newestElementRef,
        workHistoryData,
        LSPMRequestPaceInfo,
        chartsRequestInfo,
        showModal,
        isNewEntry,
        whEntries,
        selectedEntryIdx,
        setWHEntries,
        isWHEntryValid,
        handleNavigateToMyExperience,
        handleEditEntry,
        handleAddEntry,
        handleCancelChanges,
        handleSaveChanges,
        handleDeleteEntry,
    } = useYourProfile(personId);

    return (
        <>
            <Box
                className={classes.workHistoryWrapper}
                ref={wrapperRef}
                sx={{
                    ...(!isEditable
                        ? {
                              "&:hover": {
                                  cursor: "pointer",
                              },
                          }
                        : {}),
                }}
                onClick={isEditable ? undefined : handleNavigateToMyExperience}
            >
                <Box className={classes.drawerBlock}>
                    <Typography variant="h6" className={classes.drawerBlockTitle}>
                        {title}
                    </Typography>
                    <WorkHistoryTimeline
                        onClickEdit={handleEditEntry}
                        workHistoryData={workHistoryData.data}
                        shouldShowWarning={LSPMRequestPaceInfo === "error" || chartsRequestInfo === "error"}
                        requestStatus={workHistoryData.requestInfo}
                        wrapperRef={scrollerWrapperRef}
                        newestElementRef={newestElementRef}
                        isEditable={isEditable}
                    />
                    {workHistoryData.requestInfo !== "fetching" && isEditable && (
                        <>
                            <LinkButton
                                Icon={<AddCircleRoundedIcon className={classes.addRoleIcon} />}
                                label={<FormattedMessage id="wizard.createYourProfile.addRole" />}
                                onClick={handleAddEntry}
                            />
                        </>
                    )}
                </Box>
                {!isEditable && workHistoryData.requestInfo === "done" && (
                    <Box display="flex">
                        <Button
                            variant="outlined"
                            sx={{ marginBottom: "8px", flex: 1, color: "text.primary" }}
                            endIcon={<CustomIcon icon={icons.arrowRight} />}
                            startIcon={<CustomIcon icon={icons.sparkles} sx={{ fontSize: 16 }} />}
                            onClick={handleNavigateToMyExperience}
                        >
                            {intl.formatMessage({ id: "pace.myDashboard.workHistory.button" })}
                        </Button>
                    </Box>
                )}
            </Box>

            {/* Modal */}
            <Modal open={showModal}>
                <Box
                    sx={{
                        background: "#fff",
                        width: "100%",
                        maxWidth: 600,
                        pt: 3,
                        pb: 5,
                        px: 5,
                        borderRadius: 0.8,
                    }}
                >
                    <Box display="flex" alignItems="center" mb={1}>
                        <Typography variant="h6" sx={{ fontSize: 20, fontWeight: 700 }}>
                            {intl.formatMessage({
                                id: isNewEntry ? "paceLabsDashbard.newRole" : "paceLabsDashbard.editRole",
                            })}
                        </Typography>
                        <IconButton onClick={handleCancelChanges} size="large" sx={{ display: "block", ml: "auto" }}>
                            <CustomIcon icon={icons.close} />
                        </IconButton>
                    </Box>

                    <ProfileEntry
                        entry={whEntries[selectedEntryIdx]}
                        setWHEntries={setWHEntries}
                        shouldClear={false}
                        showFieldsInline={false}
                        showHelperText={true}
                    />

                    <Box display="flex">
                        <NavButton
                            icon={null}
                            onClick={handleSaveChanges}
                            disabled={!isWHEntryValid(whEntries[selectedEntryIdx])}
                            translationId={
                                isNewEntry
                                    ? "paceLabsDashbard.workHistory.modal.addRole"
                                    : "paceLabsDashbard.workHistory.modal.saveChanges"
                            }
                        />
                        <Box display="inline" ml={1} mr="auto">
                            <NavButton
                                icon={null}
                                onClick={handleCancelChanges}
                                translationId={"paceLabsDashbard.workHistory.modal.cancel"}
                                variant="outlined"
                            />
                        </Box>
                        {!isNewEntry && (
                            <NavButton
                                color="error"
                                icon={null}
                                onClick={handleDeleteEntry}
                                translationId={"paceLabsDashbard.workHistory.modal.delete"}
                                disabled={whEntries.length === 1}
                            />
                        )}
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

export default YourProfile;
